import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  Calendar,
  Package,
  Truck,
  FileText,
  Scale,
  Ruler,
  MapPin,
  Ship,
  Navigation,
  Clock,
  ArrowRight,
  Building,
  User,
  AlertCircle,
  Edit3,
  CheckCircle,
  Circle,
  Plus
} from 'lucide-react';

// Composants modaux séparés pour éviter les re-renders
const GeneralModal = ({ isOpen, tempData, setTempData, onClose, onSave }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Informations générales
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              N° dossier *
            </label>
            <input
              type="text"
              value={tempData.numeroDossier || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, numeroDossier: e.target.value }))}
              placeholder="DOS-2024-01-001"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              N° bon de livraison *
            </label>
            <input
              type="text"
              value={tempData.bonLivraison || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, bonLivraison: e.target.value }))}
              placeholder="BL-001-2024"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              N° commande client
            </label>
            <input
              type="text"
              value={tempData.numeroCommande || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, numeroCommande: e.target.value }))}
              placeholder="CMD-CLIENT-001"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Date de création
            </label>
            <input
              type="date"
              value={tempData.dateCreation || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, dateCreation: e.target.value }))}
              className="input-field"
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
            disabled={!tempData.numeroDossier || !tempData.bonLivraison}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const DepartModal = ({ isOpen, tempData, setTempData, onClose, onSave, clients }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          Lieu de départ
        </h3>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="tempDepartType"
                value="none"
                checked={(tempData.type || 'none') === 'none'}
                onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value, client: '', texte: '' }))}
                className="mr-2"
              />
              Aucun
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tempDepartType"
                value="client"
                checked={(tempData.type || 'none') === 'client'}
                onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value, texte: '' }))}
                className="mr-2"
              />
              Client
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tempDepartType"
                value="texte"
                checked={(tempData.type || 'none') === 'texte'}
                onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value, client: '' }))}
                className="mr-2"
              />
              Adresse libre
            </label>
          </div>
          
          {tempData.type === 'client' && (
            <select
              value={tempData.client || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, client: e.target.value }))}
              className="input-field"
            >
              <option value="">Sélectionner un client</option>
              {clients.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          )}
          
          {tempData.type === 'texte' && (
            <textarea
              value={tempData.texte || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, texte: e.target.value }))}
              placeholder="Adresse complète du lieu de départ"
              rows={3}
              className="input-field"
            />
          )}
          
          {tempData.type !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Date d'enlèvement prévue
              </label>
              <input
                type="date"
                value={tempData.dateEnlevement || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, dateEnlevement: e.target.value }))}
                className="input-field"
              />
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const EntrepotModal = ({ isOpen, tempData, setTempData, onClose, onSave }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Building className="h-5 w-5 mr-2" />
          Passage par l'entrepôt ATL
        </h3>
        
        <div className="space-y-4 mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={tempData.active || false}
              onChange={(e) => setTempData(prev => ({ ...prev, active: e.target.checked }))}
              className="mr-3"
            />
            <span className="text-sm text-port-gray-700">
              La livraison transite par l'entrepôt ATL
            </span>
          </label>
          
          {tempData.active && (
            <div className="pl-6 space-y-4 border-l-2 border-atl-green-200">
              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Date d'entrée prévue
                </label>
                <input
                  type="date"
                  value={tempData.dateEntree || ''}
                  onChange={(e) => setTempData(prev => ({ ...prev, dateEntree: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Date de sortie prévue
                </label>
                <input
                  type="date"
                  value={tempData.dateSortie || ''}
                  onChange={(e) => setTempData(prev => ({ ...prev, dateSortie: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const DestinationModal = ({ isOpen, tempData, setTempData, onClose, onSave, clients }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Ship className="h-5 w-5 mr-2" />
          Lieu de livraison
        </h3>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="tempDestinationType"
                value="none"
                checked={(tempData.type || 'none') === 'none'}
                onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value, client: '', texte: '' }))}
                className="mr-2"
              />
              Aucun
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tempDestinationType"
                value="client"
                checked={(tempData.type || 'none') === 'client'}
                onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value, texte: '' }))}
                className="mr-2"
              />
              Client
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tempDestinationType"
                value="texte"
                checked={(tempData.type || 'none') === 'texte'}
                onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value, client: '' }))}
                className="mr-2"
              />
              Adresse libre
            </label>
          </div>
          
          {tempData.type === 'client' && (
            <select
              value={tempData.client || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, client: e.target.value }))}
              className="input-field"
            >
              <option value="">Sélectionner un client</option>
              {clients.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          )}
          
          {tempData.type === 'texte' && (
            <textarea
              value={tempData.texte || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, texte: e.target.value }))}
              placeholder="Adresse complète du lieu de livraison"
              rows={3}
              className="input-field"
            />
          )}
          
          {tempData.type !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Date de livraison prévue
              </label>
              <input
                type="date"
                value={tempData.dateLivraison || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, dateLivraison: e.target.value }))}
                className="input-field"
              />
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const ContenuModal = ({ isOpen, tempData, setTempData, onClose, onSave, conditionnements }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Package className="h-5 w-5 mr-2" />
          Contenu de la livraison
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Description du contenu
              </label>
              <textarea
                value={tempData.description || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description détaillée..."
                rows={4}
                className="input-field"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Conditionnement
              </label>
              <select
                value={tempData.conditionnement || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, conditionnement: e.target.value }))}
                className="input-field"
              >
                <option value="">Sélectionner</option>
                {conditionnements.map(cond => (
                  <option key={cond} value={cond}>{cond}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Poids {tempData.description && '*'}
              </label>
              <input
                type="text"
                value={tempData.poids || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, poids: e.target.value }))}
                placeholder="2.5 tonnes"
                className="input-field"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Dimensions {tempData.description && '*'}
              </label>
              <input
                type="text"
                value={tempData.dimensions || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, dimensions: e.target.value }))}
                placeholder="12.2 x 2.4 x 2.6 m"
                className="input-field"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
            disabled={tempData.description && (!tempData.poids || !tempData.dimensions)}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const NouvelleLivraison = () => {
  const [formData, setFormData] = useState({
    // Informations générales
    numeroDossier: '',
    bonLivraison: '',
    numeroCommande: '',
    dateCreation: new Date().toISOString().split('T')[0],
    
    // Itinéraire
    lieuDepart: {
      type: 'none',
      client: '',
      texte: '',
      dateEnlevement: ''
    },
    passageEntrepot: {
      active: false,
      dateEntree: '',
      dateSortie: ''
    },
    lieuLivraison: {
      type: 'none',
      client: '',
      texte: '',
      dateLivraison: ''
    },
    
    // Contenu
    contenu: {
      description: '',
      poids: '',
      dimensions: '',
      conditionnement: ''
    },
    
    // Transports (dynamique selon itinéraire)
    transports: [],
    
    // Suivi
    statut: 'Créée',
    
    // Notes
    notes: ''
  });

  const [activeModal, setActiveModal] = useState(null);
  const [tempData, setTempData] = useState({});

  const clients = [
    'CMA CGM', 'MSC', 'Evergreen', 'COSCO', 'Hapag-Lloyd',
    'ONE', 'Yang Ming', 'HMM', 'ZIM', 'Maersk Line'
  ];

  const conditionnements = [
    'Conteneur 20\'', 'Conteneur 40\'', 'Conteneur 40\' HC',
    'Palette', 'Carton', 'Sachet', 'Fût', 'Autre'
  ];

  const transporteurs = [
    'Transport Express', 'Logistique Pro', 'Rapid Delivery',
    'ATL Transport', 'Maritime Solutions'
  ];

  // Calcul automatique des étapes de transport
  useEffect(() => {
    const nouvellesEtapes = [];
    
    if (formData.lieuDepart.type !== 'none') {
      if (formData.passageEntrepot.active) {
        nouvellesEtapes.push({
          id: 'depart-entrepot',
          nom: 'Enlèvement vers entrepôt',
          origine: formData.lieuDepart.type === 'client' ? formData.lieuDepart.client : formData.lieuDepart.texte,
          destination: 'Entrepôt ATL',
          transporteur: '',
          reference: ''
        });
        
        if (formData.lieuLivraison.type !== 'none') {
          nouvellesEtapes.push({
            id: 'entrepot-destination',
            nom: 'Livraison depuis entrepôt',
            origine: 'Entrepôt ATL',
            destination: formData.lieuLivraison.type === 'client' ? formData.lieuLivraison.client : formData.lieuLivraison.texte,
            transporteur: '',
            reference: ''
          });
        }
      } else if (formData.lieuLivraison.type !== 'none') {
        nouvellesEtapes.push({
          id: 'depart-destination',
          nom: 'Transport direct',
          origine: formData.lieuDepart.type === 'client' ? formData.lieuDepart.client : formData.lieuDepart.texte,
          destination: formData.lieuLivraison.type === 'client' ? formData.lieuLivraison.client : formData.lieuLivraison.texte,
          transporteur: '',
          reference: ''
        });
      }
    }
    
    setFormData(prev => ({ ...prev, transports: nouvellesEtapes }));
  }, [formData.lieuDepart, formData.passageEntrepot, formData.lieuLivraison]);

  const openModal = (modalType, currentData = {}) => {
    setTempData(currentData);
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
    setTempData({});
  };

  const saveModalData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data }
    }));
    closeModal();
  };

  const isComplete = (section) => {
    switch(section) {
      case 'general':
        return formData.numeroDossier && formData.bonLivraison;
      case 'depart':
        return formData.lieuDepart.type !== 'none' && 
               (formData.lieuDepart.client || formData.lieuDepart.texte);
      case 'entrepot':
        return !formData.passageEntrepot.active || 
               (formData.passageEntrepot.dateEntree && formData.passageEntrepot.dateSortie);
      case 'destination':
        return formData.lieuLivraison.type !== 'none' && 
               (formData.lieuLivraison.client || formData.lieuLivraison.texte);
      case 'contenu':
        return formData.contenu.description || formData.contenu.conditionnement;
      case 'transports':
        return formData.transports.length > 0;
      default:
        return false;
    }
  };

  const renderItineraireVisuel = () => {
    const etapes = [];
    
    if (formData.lieuDepart.type !== 'none') {
      etapes.push({
        nom: 'Départ',
        lieu: formData.lieuDepart.type === 'client' ? formData.lieuDepart.client : formData.lieuDepart.texte,
        type: formData.lieuDepart.type,
        date: formData.lieuDepart.dateEnlevement
      });
    }
    
    if (formData.passageEntrepot.active) {
      etapes.push({
        nom: 'Entrepôt ATL',
        lieu: 'Entrepôt ATL',
        type: 'entrepot',
        date: formData.passageEntrepot.dateEntree
      });
    }
    
    if (formData.lieuLivraison.type !== 'none') {
      etapes.push({
        nom: 'Destination',
        lieu: formData.lieuLivraison.type === 'client' ? formData.lieuLivraison.client : formData.lieuLivraison.texte,
        type: formData.lieuLivraison.type,
        date: formData.lieuLivraison.dateLivraison
      });
    }
    
    if (etapes.length === 0) {
      return (
        <div className="text-center py-8 text-port-gray-500">
          <Navigation className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Aucun itinéraire défini</p>
          <p className="text-sm">Configurez le départ et la destination</p>
        </div>
      );
    }
    
    return (
      <div className="flex items-center justify-center space-x-6 py-4">
        {etapes.map((etape, index) => (
          <React.Fragment key={index}>
            <div className="flex flex-col items-center space-y-2">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-medium ${
                etape.type === 'client' ? 'bg-atl-blue-600' :
                etape.type === 'entrepot' ? 'bg-atl-green-600' : 'bg-port-gray-600'
              }`}>
                {etape.type === 'client' ? <User className="h-6 w-6" /> :
                 etape.type === 'entrepot' ? <Building className="h-6 w-6" /> :
                 <MapPin className="h-6 w-6" />}
              </div>
              <div className="text-center">
                <div className="font-medium text-port-gray-900">{etape.nom}</div>
                <div className="text-sm text-port-gray-600 max-w-32 truncate">{etape.lieu || 'Non défini'}</div>
                {etape.date && (
                  <div className="text-xs text-port-gray-500 flex items-center mt-1">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(etape.date).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
            </div>
            {index < etapes.length - 1 && (
              <ArrowRight className="h-6 w-6 text-port-gray-400" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-port-gray-900">Nouvelle livraison</h1>
          <p className="text-port-gray-600">Créer une nouvelle livraison ATL</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="btn-secondary flex items-center space-x-2">
            <X className="h-5 w-5" />
            <span>Annuler</span>
          </button>
          <button className="btn-primary flex items-center space-x-2">
            <Save className="h-5 w-5" />
            <span>Enregistrer</span>
          </button>
        </div>
      </div>

      {/* Aperçu de l'itinéraire */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-port-gray-900 mb-4 flex items-center">
          <Navigation className="h-6 w-6 mr-2" />
          Aperçu de la livraison
        </h2>
        {renderItineraireVisuel()}
      </div>

      {/* Dashboard avec boutons */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Informations générales */}
        <div 
          onClick={() => openModal('general', {
            numeroDossier: formData.numeroDossier,
            bonLivraison: formData.bonLivraison,
            numeroCommande: formData.numeroCommande,
            dateCreation: formData.dateCreation
          })}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Informations générales</h3>
            </div>
            {isComplete('general') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-port-gray-600">N° dossier:</span>
              <span className={formData.numeroDossier ? 'text-port-gray-900 font-medium' : 'text-port-gray-400'}>
                {formData.numeroDossier || 'Non défini'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-port-gray-600">Bon livraison:</span>
              <span className={formData.bonLivraison ? 'text-port-gray-900 font-medium' : 'text-port-gray-400'}>
                {formData.bonLivraison || 'Non défini'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-port-gray-600">Commande client:</span>
              <span className={formData.numeroCommande ? 'text-port-gray-900 font-medium' : 'text-port-gray-400'}>
                {formData.numeroCommande || 'Non défini'}
              </span>
            </div>
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Lieu de départ */}
        <div 
          onClick={() => openModal('depart', formData.lieuDepart)}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MapPin className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Lieu de départ</h3>
            </div>
            {isComplete('depart') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-port-gray-600">Type:</span>
              <span className="ml-2 text-port-gray-900 font-medium capitalize">
                {formData.lieuDepart.type === 'none' ? 'Non défini' : formData.lieuDepart.type}
              </span>
            </div>
            {formData.lieuDepart.type !== 'none' && (
              <div>
                <span className="text-port-gray-600">Adresse:</span>
                <span className="ml-2 text-port-gray-900 font-medium">
                  {formData.lieuDepart.client || formData.lieuDepart.texte || 'Non définie'}
                </span>
              </div>
            )}
            {formData.lieuDepart.dateEnlevement && (
              <div>
                <span className="text-port-gray-600">Date enlèvement:</span>
                <span className="ml-2 text-port-gray-900 font-medium">
                  {new Date(formData.lieuDepart.dateEnlevement).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Passage entrepôt */}
        <div 
          onClick={() => openModal('entrepot', formData.passageEntrepot)}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Building className="h-8 w-8 text-atl-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Entrepôt ATL</h3>
            </div>
            {isComplete('entrepot') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-port-gray-600">Passage:</span>
              <span className={`ml-2 font-medium ${formData.passageEntrepot.active ? 'text-atl-green-600' : 'text-port-gray-900'}`}>
                {formData.passageEntrepot.active ? 'Oui' : 'Non'}
              </span>
            </div>
            {formData.passageEntrepot.active && (
              <>
                <div>
                  <span className="text-port-gray-600">Entrée:</span>
                  <span className="ml-2 text-port-gray-900 font-medium">
                    {formData.passageEntrepot.dateEntree ? 
                      new Date(formData.passageEntrepot.dateEntree).toLocaleDateString('fr-FR') : 
                      'Non définie'
                    }
                  </span>
                </div>
                <div>
                  <span className="text-port-gray-600">Sortie:</span>
                  <span className="ml-2 text-port-gray-900 font-medium">
                    {formData.passageEntrepot.dateSortie ? 
                      new Date(formData.passageEntrepot.dateSortie).toLocaleDateString('fr-FR') : 
                      'Non définie'
                    }
                  </span>
                </div>
              </>
            )}
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Lieu de livraison */}
        <div 
          onClick={() => openModal('destination', formData.lieuLivraison)}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Ship className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Destination</h3>
            </div>
            {isComplete('destination') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-port-gray-600">Type:</span>
              <span className="ml-2 text-port-gray-900 font-medium capitalize">
                {formData.lieuLivraison.type === 'none' ? 'Non défini' : formData.lieuLivraison.type}
              </span>
            </div>
            {formData.lieuLivraison.type !== 'none' && (
              <div>
                <span className="text-port-gray-600">Adresse:</span>
                <span className="ml-2 text-port-gray-900 font-medium">
                  {formData.lieuLivraison.client || formData.lieuLivraison.texte || 'Non définie'}
                </span>
              </div>
            )}
            {formData.lieuLivraison.dateLivraison && (
              <div>
                <span className="text-port-gray-600">Date livraison:</span>
                <span className="ml-2 text-port-gray-900 font-medium">
                  {new Date(formData.lieuLivraison.dateLivraison).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Contenu */}
        <div 
          onClick={() => openModal('contenu', formData.contenu)}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Contenu</h3>
            </div>
            {isComplete('contenu') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-port-gray-600">Description:</span>
              <span className="ml-2 text-port-gray-900 font-medium">
                {formData.contenu.description || 'Non définie'}
              </span>
            </div>
            <div>
              <span className="text-port-gray-600">Conditionnement:</span>
              <span className="ml-2 text-port-gray-900 font-medium">
                {formData.contenu.conditionnement || 'Non défini'}
              </span>
            </div>
            {formData.contenu.poids && (
              <div>
                <span className="text-port-gray-600">Poids:</span>
                <span className="ml-2 text-port-gray-900 font-medium">{formData.contenu.poids}</span>
              </div>
            )}
            {formData.contenu.dimensions && (
              <div>
                <span className="text-port-gray-600">Dimensions:</span>
                <span className="ml-2 text-port-gray-900 font-medium">{formData.contenu.dimensions}</span>
              </div>
            )}
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Transports */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Truck className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">
                Transports ({formData.transports.length})
              </h3>
            </div>
            {formData.transports.length > 0 ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          {formData.transports.length > 0 ? (
            <div className="space-y-3 text-sm">
              {formData.transports.map((transport, index) => (
                <div key={transport.id} className="border-l-2 border-atl-blue-200 pl-3">
                  <div className="font-medium text-port-gray-900">{transport.nom}</div>
                  <div className="text-port-gray-600">
                    {transport.origine} → {transport.destination}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-port-gray-500 text-sm">
              Les transports seront générés automatiquement selon l'itinéraire défini.
            </p>
          )}
        </div>
      </div>

      {/* Modals */}
      <GeneralModal 
        isOpen={activeModal === 'general'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={(data) => saveModalData('', data)}
      />
      
      <DepartModal 
        isOpen={activeModal === 'depart'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={(data) => saveModalData('lieuDepart', data)}
        clients={clients}
      />
      
      <EntrepotModal 
        isOpen={activeModal === 'entrepot'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={(data) => saveModalData('passageEntrepot', data)}
      />
      
      <DestinationModal 
        isOpen={activeModal === 'destination'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={(data) => saveModalData('lieuLivraison', data)}
        clients={clients}
      />
      
      <ContenuModal 
        isOpen={activeModal === 'contenu'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={(data) => saveModalData('contenu', data)}
        conditionnements={conditionnements}
      />
    </div>
  );
};

export default NouvelleLivraison; 